import { ReactElement } from 'react'
import styled from 'styled-components'

import { AngleDownIcon } from 'components/Icons/AngleDown'
import { SpinnerIcon } from 'components/Icons/SpinnerIcon'
import { IButtonProps, TButtonKind } from './types'

const BaseButton = styled.button<{ fullWidth?: boolean; disabled?: boolean; small?: boolean }>`
  align-items: center;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }): string => (disabled ? '0.6' : '1')};
  display: flex;
  font-size: 16px;
  font-weight: 400;
  justify-content: center;
  gap: 10px;
  padding: ${({ small }): string => (small ? '15px 23px' : '15.5px 32px')};
  text-decoration: none;
  transition: 0.2s;
  width: ${({ fullWidth }): string => (fullWidth ? '100%' : 'auto')};

  svg {
    height: 18px;
    width: 18px;
  }
`

const StyledSpinnerIcon = styled(SpinnerIcon)<{ kind?: TButtonKind }>`
  fill: ${({ kind }): string => (kind === 'terciary' ? '#5d5c70' : '')};
  margin-left: 10px;
`

const PrimaryButton = styled(BaseButton)`
  background-color: #916ee8;
  border: 1px solid #916ee8;
  color: #ffffff;

  svg {
    fill: #ffffff;
  }

  &:hover {
    background-color: #8665d8;
  }

  &:active {
    background-color: #8665d8;
  }

  &:disabled {
    background-color: #f0f0f0;
    border: 1px solid #f0f0f0;
    color: #b0b0b0;

    svg {
      fill: #b0b0b0;
    }
  }
`

const CancelButton = styled(BaseButton)`
  background-color: #ffffff;
  border: 1px solid #916ee8;
  color: #916ee8;

  svg {
    fill: #916ee8;
  }

  &:hover {
    background-color: #f4f0ff;
  }

  &:active {
    background-color: #f4f0ff;
  }

  &:disabled {
    background-color: #ffffff;
    border-color: #b0b0b0;
    color: #b0b0b0;

    svg {
      fill: #b0b0b0;
    }
  }
`

const SecondaryButton = styled(BaseButton)`
  border-radius: 17px;
  background-color: #ffffff;
  border: solid 1px #5d59d7;
  text-align: center;
  color: #5d59d7;
  padding: 8px 20px;
  font-weight: 500;

  :hover {
    background-color: #f4f0ff;
  }

  :active {
    background-color: #e0d6ff;
  }

  ${StyledSpinnerIcon} {
    fill: #5d59d7;
  }
`

const TerciaryButton = styled(BaseButton)<{ small?: boolean }>`
  border-radius: 5px;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.08);
  border: solid 1px #cacad2;
  background-color: #ffffff;
  padding: ${({ small }): string => (small ? '5px 7px' : '10px 14px')};
  font-size: ${({ small }): string => (small ? '13px' : '14px')};
  color: #5d5c70;
  justify-content: space-between;
`

const StyledAngleDownIcon = styled(AngleDownIcon)`
  fill: #9999bd !important;
  height: 6px !important;
  width: 10px !important;
`

const Button = ({
  kind = 'primary',
  children,
  iconLeft,
  iconRight,
  noIcon,
  loading,
  disabled,
  small,
  ...props
}: IButtonProps): ReactElement => {
  let Component = BaseButton
  switch (kind) {
    case 'primary':
      Component = PrimaryButton
      break
    case 'secondary':
      Component = SecondaryButton
      break
    case 'cancel':
      Component = CancelButton
      break
    case 'terciary':
      Component = TerciaryButton
      break
    default:
      return <button {...props}>{children}</button>
  }

  return (
    <Component disabled={disabled || loading} small={small} type={props.type || 'submit'} {...props}>
      {iconLeft ? iconLeft : null}
      {children}
      {loading ? <StyledSpinnerIcon /> : null}
      {iconRight && !loading ? iconRight : null}
      {kind === 'terciary' && !noIcon && <StyledAngleDownIcon />}
    </Component>
  )
}

export { Button }
